import React, { useEffect, useMemo } from "react";
import { Modal, Button, Form, Col } from "react-bootstrap";
import { useFormik } from "formik";
import toast from "react-hot-toast";
import Select from "react-select";
import { planSchema } from "../../../../../helpers/yup_validator";
import { post, put } from "../../../../../helpers/api_helper";
import { useDispatch } from "react-redux";
import { setPlanCreated } from "../../../../../store/plan";
import { CKEditor } from "@ckeditor/ckeditor5-react";
import { ClassicEditor } from "ckeditor5";
import CKEditorComponent from "../../../../../components/CKeditor/Ckeditor";
import { uploadPlugin } from "../../../../../helpers/common";
import { useGetUsersQuery } from "../../../../../store/services/user.service";
import { PLAN_ADD, PLAN_EDIT } from "../../../../../helpers/url_helper";
import { useGetAllUsersQuery } from "../../../../../store/services/utility.service";

const AddNewPlan = ({
  show,
  onHide,
  mode,
  planData,
  setRefresh = () => { },
  resetFormOnClose,
}) => {
  const dispatch = useDispatch();

  const {data: users, isLoading } = useGetAllUsersQuery();
  // const users = userListing?.items;

  const userOptions = useMemo(() => {
    if(!users?.length) {
      return [];
    }

    return users.map((user) => ({
      label: `${user.name} (${user.email})`,
      value: user?._id
    }))
  }, [users])

  const formik = useFormik({
    initialValues: {
      name: "",
      stripe_price_id: "",
      stripe_product_id: "",
      description: "",
      price: 0,
      type: "month",
      features: "",
      duration: 0,
      free_trial: "no",
      free_trial_duration: 0,
      visibility: "public",
      user_ids: [],
      status: "active",
    },
    validationSchema: planSchema,
    enableReinitialize: true,
    onSubmit: async (values, { setSubmitting, setErrors }) => {
      try {
        const payload = {
          ...values,
          user_ids: values.user_ids?.map(user => user.value)
        }
        
        if (mode === "edit") {
          await put(`${PLAN_EDIT}/${planData._id}`, payload);
          toast.success("Plan updated successfully");
        } else {
          await post(PLAN_ADD, payload);
          toast.success("Plan created successfully");
          dispatch(setPlanCreated(true));
          formik.resetForm();
        }
        onHide();
        setRefresh();
      } catch (error) {
        console.error("Error submitting plan data:", error);
        const errorMsg = error.response?.data?.message
          ? error.response?.data?.message
          : error.response?.data?.error || "An error occurred.";
        setErrors({ submissionError: errorMsg });
        toast.error(errorMsg);
      } finally {
        setSubmitting(false);
      }
    },
  });

  useEffect(() => {
    if (mode === "edit" && planData) {
      const selectedUsers = planData.user_ids.map(user => ({
        label: `${user.name} (${user.email})`,
        value: user._id
      }));
      formik.setValues({
        name: planData?.name || "",
        stripe_price_id: planData?.stripe_price_id || "",
        stripe_product_id: planData?.stripe_product_id || "",
        description: planData?.description || "",
        price: planData?.price || 0,
        type: planData?.type || "month",
        features: planData?.features || "",
        duration: planData?.duration || 0,
        free_trial: planData?.free_trial || "no",
        free_trial_duration: planData?.free_trial_duration || 0,
        visibility: planData?.visibility || "public",
        user_ids: selectedUsers,
        status: planData?.status || "active",
      });
    } else if (resetFormOnClose) {
      formik.resetForm();
    }
  }, [mode, planData, resetFormOnClose, userOptions]);
  return (
    <Modal show={show} onHide={onHide} centered>
      <Modal.Header closeButton>
        <Modal.Title>
          {mode === "edit"
            ? "Edit Subscription Plan"
            : "Create New Subscription Plan"}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form onSubmit={formik.handleSubmit}>
          {formik.errors.submissionError && (
            <div className="alert alert-danger">
              {formik.errors.submissionError}
            </div>
          )}
          <Form.Group className="py-2" controlId="planName">
            <Form.Label>Plan Name</Form.Label>
            <Form.Control
              type="text"
              placeholder="Enter plan name"
              name="name"
              value={formik.values.name}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              isInvalid={formik.touched.name && formik.errors.name}
            />
            {formik.touched.name && formik.errors.name ? (
              <Form.Control.Feedback type="invalid">
                {formik.errors.name}
              </Form.Control.Feedback>
            ) : null}
          </Form.Group>
          <Form.Group className="py-2" controlId="planStripePriceId">
            <Form.Label>Stripe Price ID</Form.Label>
            <Form.Control
              type="text"
              placeholder="Enter Stripe Price ID"
              name="stripe_price_id"
              value={formik.values.stripe_price_id}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              isInvalid={
                formik.touched.stripe_price_id && formik.errors.stripe_price_id
              }
            />
            {formik.touched.stripe_price_id && formik.errors.stripe_price_id ? (
              <Form.Control.Feedback type="invalid">
                {formik.errors.stripe_price_id}
              </Form.Control.Feedback>
            ) : null}
          </Form.Group>
          <Form.Group className="py-2" controlId="planStripePriceId">
            <Form.Label>Stripe Product ID</Form.Label>
            <Form.Control
              type="text"
              placeholder="Enter Stripe Price ID"
              name="stripe_product_id"
              value={formik.values.stripe_product_id}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              isInvalid={
                formik.touched.stripe_product_id && formik.errors.stripe_product_id
              }
            />
            {formik.touched.stripe_product_id && formik.errors.stripe_product_id ? (
              <Form.Control.Feedback type="invalid">
                {formik.errors.stripe_product_id}
              </Form.Control.Feedback>
            ) : null}
          </Form.Group>
          <Form.Group className="py-2" controlId="planDescription">
            <Form.Label>Description</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              placeholder="Enter plan description"
              name="description"
              value={formik.values.description}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              isInvalid={
                formik.touched.description && formik.errors.description
              }
            />
            {formik.touched.description && formik.errors.description ? (
              <Form.Control.Feedback type="invalid">
                {formik.errors.description}
              </Form.Control.Feedback>
            ) : null}
          </Form.Group>
          <Form.Group className="py-2" controlId="planPrice">
            <Form.Label>Plan Price</Form.Label>
            <Form.Control
              type="number"
              placeholder="Enter plan price"
              name="price"
              value={formik.values.price}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              isInvalid={formik.touched.price && formik.errors.price}
            />
            {formik.touched.price && formik.errors.price ? (
              <Form.Control.Feedback type="invalid">
                {formik.errors.price}
              </Form.Control.Feedback>
            ) : null}
          </Form.Group>
          <Form.Group className="py-2" controlId="planType">
            <Form.Label>Type</Form.Label>
            <Form.Control
              as="select"
              name="type"
              value={formik.values.type}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              isInvalid={formik.touched.type && formik.errors.type}
            >
              <option value="month">Month</option>
              <option value="quarter">Quarter</option>
              <option value="year">Year</option>
              <option value="weekly">Weekly</option>
            </Form.Control>
            {formik.touched.type && formik.errors.type ? (
              <Form.Control.Feedback type="invalid">
                {formik.errors.type}
              </Form.Control.Feedback>
            ) : null}
          </Form.Group>
          <Form.Group className="py-2" controlId="planFeatures">
            <Form.Label>Features</Form.Label>
            <CKEditorComponent
              content={formik.values.features}
              onChange={(data) => {
                formik.setFieldValue("features", data);
              }}
              onBlur={() => formik.setFieldTouched("features", true)}
              uploadPlugin={uploadPlugin}
              rows={8}
            />
            {formik.touched.features && formik.errors.features ? (
              <div className="text-danger">{formik.errors.features}</div>
            ) : null}
          </Form.Group>
          <Form.Group className="py-2" controlId="planDuration">
            <Form.Label>Duration (in months)</Form.Label>
            <Form.Control
              type="number"
              placeholder="Enter duration"
              name="duration"
              value={formik.values.duration}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              isInvalid={formik.touched.duration && formik.errors.duration}
            />
            {formik.touched.duration && formik.errors.duration ? (
              <Form.Control.Feedback type="invalid">
                {formik.errors.duration}
              </Form.Control.Feedback>
            ) : null}
          </Form.Group>
          <Form.Group className="py-2" controlId="planFreeTrial">
            <Form.Label>Free Trial</Form.Label>
            <Form.Control
              as="select"
              name="free_trial"
              value={formik.values.free_trial}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              isInvalid={formik.touched.free_trial && formik.errors.free_trial}
            >
              <option value="yes">Yes</option>
              <option value="no">No</option>
            </Form.Control>
            {formik.touched.free_trial && formik.errors.free_trial ? (
              <Form.Control.Feedback type="invalid">
                {formik.errors.free_trial}
              </Form.Control.Feedback>
            ) : null}
          </Form.Group>
          {formik.values.free_trial === "yes" && (
            <Form.Group className="py-2" controlId="planFreeTrialDuration">
              <Form.Label>Free Trial Duration (in days)</Form.Label>
              <Form.Control
                type="number"
                placeholder="Enter free trial duration"
                name="free_trial_duration"
                value={formik.values.free_trial_duration}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                isInvalid={
                  formik.touched.free_trial_duration &&
                  formik.errors.free_trial_duration
                }
              />
              {formik.touched.free_trial_duration &&
                formik.errors.free_trial_duration ? (
                <Form.Control.Feedback type="invalid">
                  {formik.errors.free_trial_duration}
                </Form.Control.Feedback>
              ) : null}
            </Form.Group>
          )}
          <Form.Group>
            <Form.Label>Visibility</Form.Label>
            <div className="d-flex gap-10 align-items-center">
              <Form.Group className="py-2" controlId="planVisiblityPublic">
                <Form.Check
                  type="radio"
                  label="Public"
                  name="visibility"
                  value={"public"}
                  checked={formik.values.visibility === "public"}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  isInvalid={formik.touched.visibility && formik.errors.visibility}
                />
              </Form.Group>
              <Form.Group className="py-2" controlId="planVisiblityCustom">
                <Form.Check
                  type="radio"
                  label="Custom"
                  name="visibility"
                  value={"custom"}
                  checked={formik.values.visibility === "custom"}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  isInvalid={formik.touched.visibility && formik.errors.visibility}
                />
              </Form.Group>
            </div>
            {formik.touched.visibility && formik.errors.visibility ? (
              <Form.Control.Feedback type="invalid">
                {formik.errors.visibility}
              </Form.Control.Feedback>
            ) : null}
          </Form.Group>
          {formik.values.visibility === "custom" && (
            <Form.Group className="py-2" controlId="planStatus">
              <Form.Label>Select a user to assign the plan</Form.Label>
              <Select
                id="users"
                value={formik.values.user_ids}
                options={userOptions}
                onChange={(selected) =>
                  formik.setFieldValue("user_ids", selected ? selected : [])
                }
                isClearable={true}
                isDisabled={formik.isSubmitting}
                maxMenuHeight={150}
                isMulti={true}
              />
            </Form.Group>
          )}
          <Form.Group className="py-2" controlId="planStatus">
            <Form.Label>Status</Form.Label>
            <Form.Control
              as="select"
              name="status"
              value={formik.values.status}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              isInvalid={formik.touched.status && formik.errors.status}
            >
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
              <option value="archived">Archived</option>
            </Form.Control>
            {formik.touched.status && formik.errors.status ? (
              <Form.Control.Feedback type="invalid">
                {formik.errors.status}
              </Form.Control.Feedback>
            ) : null}
          </Form.Group>
          <Col lg="12" className="my-2">
            <Button
              className="d-flex align-items-center w-100 justify-content-center commonBtn"
              type="submit"
              disabled={formik.isSubmitting}
            >
              {mode === "edit" ? "Update Plan" : "Create Plan"}
            </Button>
          </Col>
        </Form>
      </Modal.Body>
    </Modal>
  );
};

export default AddNewPlan;
